import { Crud } from "@/api";
import { PriceComparisonLine } from "@/api/stripe.api";
import { UserApi } from "@/api/user.api";
import { CartLineItem } from "@/components/CartLineItem/CartLineItem";
import { CheckoutContext } from "@/components/CheckoutForm/utils";
// PERF (2026-07-06): CartPdf + @react-pdf/renderer are now dynamically
// imported inside the Download-PDF click handler below — the static imports
// here dragged all of react-pdf into the eager /checkout entry bundle even
// though it's only needed when the button is clicked.
import { $activeAdminCartId } from "@/stores/adminWorkspace";
import { CartService } from "@/stores/cart";
import { $sharedCartSession } from "@/stores/sharedCart";
import { cn } from "@/utils/cn";
import { NY_SALES_TAX_RATE, TRIBECA_ADDRESS } from "@/utils/constants";
import { formatStripePrice } from "@/utils/functions/formatPrice";
import { usePending } from "@/utils/hooks/usePending.js";
import { Cart as ICart, LineItem } from "@/utils/types/types";
import {
  ChevronDownIcon,
  PlusIcon,
  ShareIcon,
} from "@heroicons/react/24/solid";
import { Clipboard } from "@phosphor-icons/react";
import {
  Button,
  Collapse,
  Divider,
  Loader,
  Switch,
  TextInput,
} from "@mantine/core";
import { useStore } from "@nanostores/react";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useRevalidator } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import { useDisclosure } from "@mantine/hooks";
import CustomItemModal from "../CustomItemModal/CustomItemModal";

type CheckoutCartProps = {
  cart: ICart;
  ctx: CheckoutContext;
};

const roundToCents = (value: number) => Math.round(value);

const isNyTaxableScenario = (data: CheckoutContext["data"]) => {
  if (
    data.shippingType === "pickup" ||
    data.shippingType === "oversized" ||
    data.shippingType === "multiple-calculated"
  ) {
    return true;
  }
  if (data.shippingType === "shipToMyAddress") {
    return (data.billingState || "").trim().toUpperCase() === "NY";
  }
  if (data.shippingType === "shipToOtherAddress") {
    return (data.shippingState || "").trim().toUpperCase() === "NY";
  }
  return false;
};

const getTaxableLineItemSubtotalCents = (
  lineItems: LineItem[],
  priceComparison?: PriceComparisonLine[]
) => {
  const priceMap = new Map(
    (priceComparison || []).map(pc => [pc.lineItemId, pc])
  );
  return lineItems.reduce((total, item) => {
    if ((item.itemJson as any)?.skipTax) return total;
    const itemJson = item.itemJson as any;
    const storedCents = roundToCents(parseFloat(itemJson.price || "0") * 100);
    const comparison = priceMap.get(item.id);
    const priceCents =
      comparison?.chargedFrom === "quote" && comparison.quotedCents != null
        ? comparison.quotedCents
        : storedCents;
    return total + priceCents * (item.quantity || 0);
  }, 0);
};

// Full product subtotal (all line items, including skip-tax ones) in cents.
// Same price resolution as the taxable helper above, minus the skipTax filter.
// Used for the displayed Subtotal so it always reflects the products only and
// never the delivery-tax line item that the finalized Stripe session folds
// into checkout.total.subtotal.
const getLineItemSubtotalCents = (
  lineItems: LineItem[],
  priceComparison?: PriceComparisonLine[]
) => {
  const priceMap = new Map(
    (priceComparison || []).map(pc => [pc.lineItemId, pc])
  );
  return lineItems.reduce((total, item) => {
    const itemJson = item.itemJson as any;
    const storedCents = roundToCents(parseFloat(itemJson.price || "0") * 100);
    const comparison = priceMap.get(item.id);
    const priceCents =
      comparison?.chargedFrom === "quote" && comparison.quotedCents != null
        ? comparison.quotedCents
        : storedCents;
    return total + priceCents * (item.quantity || 0);
  }, 0);
};

const applyDiscountToTaxableBase = ({
  taxableBaseCents,
  subtotalCents,
  discountCents,
}: {
  taxableBaseCents: number;
  subtotalCents: number;
  discountCents: number;
}) => {
  if (taxableBaseCents <= 0 || subtotalCents <= 0 || discountCents <= 0) {
    return Math.max(0, taxableBaseCents);
  }
  const safeDiscountCents = Math.min(discountCents, subtotalCents);
  const discountShareOnTaxableBase = roundToCents(
    (safeDiscountCents * taxableBaseCents) / subtotalCents
  );
  return Math.max(0, taxableBaseCents - discountShareOnTaxableBase);
};

// Temporary admin-only verification panel: shows the stored visualizer price
// next to the live /quote price per line (with deltas) so admins can confirm
// the /quote integration before fully trusting it. Remove once verified.
function PriceComparisonPanel({ rows }: { rows: PriceComparisonLine[] }) {
  // Collapsed by default so the panel doesn't cover item details/notes in the
  // checkout cart; header stays visible with the total delta at a glance.
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const storedTotal = rows.reduce(
    (sum, r) => sum + r.storedCents * r.quantity,
    0
  );
  const quotedTotal = rows.reduce(
    (sum, r) =>
      sum +
      (r.quotedCents != null ? r.quotedCents : r.storedCents) * r.quantity,
    0
  );
  const totalDelta = quotedTotal - storedTotal;
  const anyFallback = rows.some(r => !r.quoteOk);
  const chargingFrom = rows.some(r => r.chargedFrom === "quote")
    ? "Charging /quote unit price"
    : "Charging stored visualizer price (shadow mode)";
  const deltaClass = (delta: number | null) =>
    delta == null
      ? "text-[#94a3b8]"
      : Math.abs(delta) > 25
        ? "text-[#dc2626] font-semibold"
        : delta === 0
          ? "text-[#16a34a]"
          : "text-[#ca8a04]";
  const fmtDelta = (delta: number | null) => {
    if (delta == null) return "—";
    const sign = delta > 0 ? "+" : "";
    return `${sign}${formatStripePrice(delta)}`;
  };
  return (
    <div className="mt-3 rounded-md border border-[#fbbf24] bg-[#fffbeb] p-2">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between"
      >
        <p className="text-sm font-semibold text-[#92400e]">
          Pricing check (admin)
        </p>
        <span className="text-[11px] font-semibold text-[#92400e] underline">
          {open ? "Hide" : `Δ ${fmtDelta(totalDelta)} — Show`}
        </span>
      </button>
      {open && (
        <>
          <div className="mb-2 mt-2 flex items-center justify-between">
            <p className="text-[11px] text-[#92400e]">{chargingFrom}</p>
            {anyFallback && (
              <span className="rounded-full bg-[#fde68a] px-2 py-0.5 text-[11px] font-semibold text-[#92400e]">
                Some lines fell back to stored
              </span>
            )}
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#92400e]">
                <th className="py-1 font-medium">Item</th>
                <th className="py-1 text-center font-medium">Qty</th>
                <th className="py-1 text-right font-medium">Visualizer</th>
                <th className="py-1 text-right font-medium">Quote API</th>
                <th className="py-1 text-right font-medium">Δ / unit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <Fragment key={r.lineItemId}>
                  <tr
                    className="cursor-pointer border-t border-[#fde68a] hover:bg-[#fef3c7]"
                    onClick={() =>
                      setExpandedId(id =>
                        id === r.lineItemId ? null : r.lineItemId
                      )
                    }
                    title="Click to see what was sent to /quote"
                  >
                    <td className="py-1 pr-2 text-[#451a03]">
                      {expandedId === r.lineItemId ? "▾ " : "▸ "}
                      {r.name}
                    </td>
                    <td className="py-1 text-center text-[#451a03]">
                      {r.quantity}
                    </td>
                    <td className="py-1 text-right text-[#451a03]">
                      {formatStripePrice(r.storedCents)}
                    </td>
                    <td className="py-1 text-right text-[#451a03]">
                      {r.quotedCents != null ? (
                        formatStripePrice(r.quotedCents)
                      ) : (
                        <span className="text-[#dc2626]">fallback</span>
                      )}
                    </td>
                    <td
                      className={`py-1 text-right ${deltaClass(r.deltaCents)}`}
                    >
                      {fmtDelta(r.deltaCents)}
                    </td>
                  </tr>
                  {expandedId === r.lineItemId && (
                    <tr className="bg-[#fffdf5]">
                      <td colSpan={5} className="py-1">
                        <div className="grid gap-2 md:grid-cols-2">
                          <div>
                            <p className="mb-1 text-[11px] font-semibold text-[#92400e]">
                              Sent to /quote
                            </p>
                            <pre className="max-h-56 overflow-auto rounded bg-[#fef3c7] p-2 text-[10px] leading-snug text-[#451a03]">
                              {JSON.stringify(r.quoteItem ?? null, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <p className="mb-1 text-[11px] font-semibold text-[#92400e]">
                              /quote breakdown
                            </p>
                            <pre className="max-h-56 overflow-auto rounded bg-[#fef3c7] p-2 text-[10px] leading-snug text-[#451a03]">
                              {JSON.stringify(r.breakdown ?? null, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#fbbf24] font-semibold text-[#451a03]">
                <td className="py-1" colSpan={2}>
                  Total (× qty)
                </td>
                <td className="py-1 text-right">
                  {formatStripePrice(storedTotal)}
                </td>
                <td className="py-1 text-right">
                  {formatStripePrice(quotedTotal)}
                </td>
                <td className={`py-1 text-right ${deltaClass(totalDelta)}`}>
                  {fmtDelta(totalDelta)}
                </td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </div>
  );
}

// On mobile, smoothly animate the cart body's height as it expands/collapses.
// On desktop the body is rendered unwrapped so its flex/overflow scroll layout
// is preserved. Defined at module scope so it keeps a stable identity across
// renders (an inline component would remount the body on every render).
function MaybeAnimated({
  isMobile,
  isExpanded,
  children,
}: {
  isMobile: boolean | undefined;
  isExpanded: boolean;
  children: ReactNode;
}) {
  if (!isMobile) return <>{children}</>;
  return (
    <Collapse in={isExpanded} transitionDuration={300}>
      <div className="flex flex-col gap-6">{children}</div>
    </Collapse>
  );
}

export default function CheckoutCart({ cart, ctx }: CheckoutCartProps) {
  const lineItems = cart.lineItems;
  // Secret used to let the visualizer fetch this shared cart when editing an
  // item. Prefer the cart's own secret, falling back to the session the
  // checkout loader seeded from the share-link URL on this page load.
  const shareSecret = cart.shareSecret || $sharedCartSession.get().secret;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const printCount = lineItems.reduce(
    (a: number, item) => a + item.quantity,
    0
  );
  const applyPromotionCode = usePending(ctx.checkout.applyPromotionCode);
  const revalidator = useRevalidator();
  const isRevalidatingCheckout = revalidator.state !== "idle";
  const [isCalculatingPrices, setIsCalculatingPrices] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCheckoutLink, setShowCheckoutLink] = useState(false);
  const [pendingCartChanges, setPendingCartChanges] = useState<{
    taxExempt?: boolean;
    skipShipping?: boolean;
  }>({});
  const [generatedCheckoutLink, setGeneratedCheckoutLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  // State for tracking line item updates (similar to Cart.tsx)
  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    Pick<LineItem, "id" | "quantity" | "note">[]
  >([]);
  const isPendingUpdate =
    lineItemsToUpdate.length > 0 || Object.keys(pendingCartChanges).length > 0;
  const activeAdminCartId = useStore($activeAdminCartId);
  const isShareable =
    Boolean(activeAdminCartId) && activeAdminCartId === cart.id;
  const pendingDeleteIds = useMemo(
    () =>
      new Set(
        lineItemsToUpdate
          .filter(item => item.quantity <= 0)
          .map(item => item.id)
      ),
    [lineItemsToUpdate]
  );
  // True when every pending change is a note edit only (quantity unchanged).
  // Note-only saves can skip price recalculation and page revalidation.
  const isNoteOnlyUpdate = useMemo(
    () =>
      lineItemsToUpdate.length > 0 &&
      lineItemsToUpdate.every(pending => {
        const original = lineItems.find(item => item.id === pending.id);
        return original !== undefined && pending.quantity === original.quantity;
      }),
    [lineItemsToUpdate, lineItems]
  );
  // Batch update function for line items
  const calculateUpdatedPrices = usePending(async () => {
    if (!isPendingUpdate) return;
    setIsCalculatingPrices(true);
    try {
      if (isShareable) {
        await Promise.all(
          lineItemsToUpdate.map(({ id, quantity, note }) => {
            if (quantity > 0) {
              return Crud.carts.updateLineItem(cart.id, id, { quantity, note });
            }
            return Crud.carts.removeLineItem(cart.id, id);
          })
        );
        await CartService.getCart();
      } else {
        await Promise.all(
          lineItemsToUpdate.map(({ id, quantity, note }) => {
            if (quantity > 0)
              return UserApi.updateLineItem({ id, quantity, note });
            return UserApi.removeLineItem({ id });
          })
        );
      }
      setLineItemsToUpdate([]);
      if (Object.keys(pendingCartChanges).length > 0) {
        // Phase 2 (2026-08-16): shared-session patches present the secret
        // (backend carts gate: owner / admin / shareSecret).
        const shared = $sharedCartSession.get();
        const authedChanges =
          shared.cartId && shared.secret && cart.id === shared.cartId
            ? { ...pendingCartChanges, shareSecret: shared.secret }
            : pendingCartChanges;
        await Crud.carts.update(cart.id, authedChanges as any);
        setPendingCartChanges({});
      }
      if (!isNoteOnlyUpdate) {
        revalidator.revalidate();
      }
    } finally {
      setIsCalculatingPrices(false);
    }
  });
  // Individual line item update handler
  const handleLineItemUpdate = ({
    id,
    quantity,
    note,
  }: {
    id: string;
    quantity: number;
    note: string;
  }) => {
    const items = lineItemsToUpdate
      .filter(el => el.id !== id)
      .concat({ id, quantity: quantity, note: note });
    setLineItemsToUpdate(items);
  };
  const handleLineItemDelete = (id: string) => {
    // Stage delete/un-delete and apply only when user clicks "Update Cart".
    setLineItemsToUpdate(items => {
      const existing = items.find(item => item.id === id);
      if (existing?.quantity === 0) {
        return items.filter(item => item.id !== id);
      }
      const lineItem = lineItems.find(item => item.id === id);
      const note = existing?.note ?? lineItem?.note ?? "";
      return items
        .filter(item => item.id !== id)
        .concat({ id, quantity: 0, note });
    });
  };
  const confirmPayment = usePending(async function () {
    try {
      if (ctx.isFinalizingSession) {
        throw new Error(
          "Checkout totals are updating. Please wait a moment and try again."
        );
      }
      // FIX 2026-08-05 (AU customer blocked 3 days): reloading the checkout
      // page mints a FRESH session with isFinalized:"false" while
      // sessionStorage restored the customer straight to this Pay button.
      // The old guard threw "totals are still syncing — wait a moment",
      // but nothing ever re-finalizes on its own, so waiting could never
      // satisfy it. Recover instead: re-run the finalize step right here,
      // then continue — or ask for ONE explicit re-press when the session
      // was swapped (never auto-confirm across a session swap; the money
      // path stays single-shot, no auto-retry — 2R).
      if (!ctx.isSessionFinalized) {
        const finalizeResult = await ctx.finalizeReviewSession();
        if (finalizeResult?.type === "session_replaced") {
          throw new Error(
            "Your order totals were just refreshed. Please press Pay once more to complete your purchase."
          );
        }
        if (
          finalizeResult?.type !== "finalized" &&
          finalizeResult?.type !== "already_finalized"
        ) {
          throw new Error(
            "We couldn't confirm your order totals. Please reopen the Review Order step, press Save & Continue, then try Pay again."
          );
        }
        // "finalized" / "already_finalized": this session is confirmed
        // finalized — safe to continue.
      }
      if (isPendingUpdate || isRevalidatingCheckout) {
        throw new Error(
          "Cart is still updating. Please wait for checkout to refresh before paying."
        );
      }
      // Validate required fields
      if (!ctx.data.firstName || !ctx.data.lastName) {
        throw new Error("First name and last name are required");
      }
      if (!ctx.data.email) {
        throw new Error("Email is required");
      }
      if (!ctx.data.phoneNumber) {
        throw new Error("Phone number is required");
      }
      if (ctx.data.shippingType === "pickup") {
        await Promise.all([
          ctx.checkout.updateShippingAddress({
            name:
              (ctx.data.billingAddress as { name?: string })?.name ||
              `${ctx.data.firstName} ${ctx.data.lastName}`,
            address: TRIBECA_ADDRESS.address,
          }),
          ctx.checkout.updateShippingOption(ctx.shippingPickupOption.value),
        ]);
      }
      const res = await ctx.confirm();
      if (res.type === "error") {
        const stripeMessage = (res.error?.message || "").trim();
        // Swap Stripe's blunt generic decline for warmer copy, but keep its
        // more specific messages (incorrect CVC, expired card, insufficient
        // funds) since those tell the customer exactly how to fix it.
        const isGenericDecline = /card (has been|was) declined\.?$/i.test(
          stripeMessage
        );
        throw new Error(
          isGenericDecline
            ? "We're sorry — your card was declined. No payment went through. Please try another card, or contact your bank."
            : stripeMessage ||
                "We couldn't process your payment. Please check your payment details and try again."
        );
      }
      alert("Payment successful! Your order has been confirmed.");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to confirm payment. Try again later.";
      alert(errorMessage);
    }
  });
  const checkout = ctx.checkout;
  // Always use Stripe checkout totals for subtotal/discount while tax preview is local.
  const pricing = useMemo(() => {
    return {
      subtotal: checkout.total?.subtotal?.minorUnitsAmount || 0,
      discount: checkout.total?.discount?.minorUnitsAmount || 0,
    };
  }, [checkout.total]);
  // A discount is already locked into the Stripe session (applied client-side
  // or baked in at finalize). A session finalized with a discount rejects
  // further promotion codes, so lock the input instead of letting a doomed
  // re-apply spin forever.
  const promoApplied = (checkout.total?.discount?.minorUnitsAmount || 0) > 0;
  // Get selected shipping option price from updated shipping options
  const selectedShippingPrice = useMemo(() => {
    const selectedOption = ctx.shippingOptions.find(
      option => option.value === ctx.data.shippingOption
    );
    return selectedOption?.price || 0;
  }, [ctx.shippingOptions, ctx.data.shippingOption]);
  // Prefer Stripe checkout totals so displayed payable amount matches charge/invoice.
  const stripeTax =
    (checkout.total?.taxExclusive?.minorUnitsAmount || 0) +
    (checkout.total?.taxInclusive?.minorUnitsAmount || 0);
  const stripeShippingPrice = checkout.total
    ? Math.max(
        0,
        (checkout.total.total?.minorUnitsAmount || 0) -
          (checkout.total.subtotal?.minorUnitsAmount || 0) -
          stripeTax +
          (checkout.total.discount?.minorUnitsAmount || 0)
      )
    : selectedShippingPrice;
  const isSkipShipping =
    pendingCartChanges.skipShipping !== undefined
      ? pendingCartChanges.skipShipping
      : !!(cart as any).skipShipping;
  const isTaxExempt =
    pendingCartChanges.taxExempt !== undefined
      ? pendingCartChanges.taxExempt
      : !!(cart as any).taxExempt;
  const isStudioArrangedShipping =
    ctx.data.shippingType === "oversized" ||
    ctx.data.shippingType === "multiple-calculated";
  const isShipToOtherAddress = ctx.data.shippingType === "shipToOtherAddress";
  const hasOtherAddressZipcode = Boolean(ctx.data.shippingZipcode?.trim());
  const shouldHoldShippingAtZero =
    isShipToOtherAddress && !hasOtherAddressZipcode;
  const taxableScenario = isNyTaxableScenario(ctx.data);
  // Tax-display gate (2026-07-22): a fresh checkout defaults shippingType to
  // "pickup" (utils.ts initial data), and isNyTaxableScenario treats pickup
  // as taxable — so brand-new customers saw NY tax (8.875%) before entering
  // any address (customer complaint 7/21). Until the customer has actually
  // COMPLETED the Shipping step, hold the Sales Tax line at "Calculated at
  // address" and keep tax out of the displayed Total. DISPLAY ONLY: the
  // Stripe session, finalize, and every post-shipping-step state are
  // unchanged. Quote-arranged carts (oversized / multiple-calculated) are
  // exempt from the gate — entering those states intentionally taxes at the
  // studio address from the start. "Shipping step completed" = CheckoutForm's
  // monotonic maxPassedStep (persisted in sessionStorage) reaching 3;
  // ctx.activeStep covers the same render on which it advances.
  const shippingStepCompleted =
    Number(ctx.activeStep) >= 3 ||
    (typeof window !== "undefined" &&
      Number(sessionStorage.getItem("checkout_max_passed_step") || "1") >= 3);
  const taxPendingShippingStep =
    !shippingStepCompleted && ctx.data.shippingType === "pickup";
  // Shipping-display gate (2026-10-10, Michael): before the customer reaches
  // the Shipping step there is no destination, live UPS rating is skipped and
  // the options carry the old GRID price (e.g. $15.95 on a small print) — the
  // summary showed it as if it were the rate. Until the Shipping step has
  // been reached, show "Calculated at address" and keep shipping out of the
  // displayed Total. DISPLAY ONLY (same contract as the tax gate above):
  // the Stripe session and what is charged are unchanged. Skip-shipping and
  // studio-quoted carts are exempt (they show $0 as before).
  const shippingStepReached =
    Number(ctx.activeStep) >= 2 ||
    (typeof window !== "undefined" &&
      Number(sessionStorage.getItem("checkout_max_passed_step") || "1") >= 2);
  const shippingPendingAddress =
    !shippingStepReached && !isSkipShipping && !isStudioArrangedShipping;
  const taxableLineItemSubtotalCents = useMemo(
    () => getTaxableLineItemSubtotalCents(lineItems, ctx.priceComparison),
    [lineItems, ctx.priceComparison]
  );
  const lineItemSubtotalCents = useMemo(
    () => getLineItemSubtotalCents(lineItems, ctx.priceComparison),
    [lineItems, ctx.priceComparison]
  );
  const discountedTaxableBaseCents = useMemo(
    () =>
      applyDiscountToTaxableBase({
        taxableBaseCents: taxableLineItemSubtotalCents,
        subtotalCents: pricing.subtotal,
        discountCents: pricing.discount,
      }),
    [taxableLineItemSubtotalCents, pricing.discount, pricing.subtotal]
  );
  const productTaxCents =
    !taxPendingShippingStep && taxableScenario && !isTaxExempt
      ? roundToCents(discountedTaxableBaseCents * NY_SALES_TAX_RATE)
      : 0;
  // F1 + oversized (2026-07-18): in the quote-arranged states a selected
  // Manhattan Messenger is a real, priced method — show its price (the SDK
  // charges it, so hiding it understated the displayed total). Any other
  // selection there stays $0: the studio quotes after the order.
  const selectedIsMessenger =
    (ctx.data.shippingType === "multiple-calculated" ||
      ctx.data.shippingType === "oversized") &&
    ctx.shippingOptions.some(
      option =>
        option.value === ctx.data.shippingOption &&
        option.label === "manhattan-messenger"
    );
  const effectiveShippingPrice =
    isSkipShipping || (isStudioArrangedShipping && !selectedIsMessenger)
      ? 0
      : shouldHoldShippingAtZero || shippingPendingAddress
        ? 0
        : selectedShippingPrice > 0
          ? selectedShippingPrice
          : stripeShippingPrice;
  // PIN #11 display fix (2026-07-20): NY taxes the delivery charge too
  // (TB-ST-838). The backend records it as a "NY Sales Tax on Delivery" line
  // item at finalize (finalizeCheckout), which Stripe folds into
  // checkout.total.subtotal — so the cart was hiding it inside Subtotal and
  // showing product-only tax on the Sales Tax line. Re-bucket for DISPLAY
  // ONLY: show the true product Subtotal computed from the cart line items
  // (never the sometimes-folded Stripe subtotal), and put the full tax —
  // product tax + delivery tax — on the Sales Tax line. Sourcing Subtotal from
  // the line items keeps it correct in every state (pre-finalize, finalized,
  // and after navigating back to change delivery) with no dependence on
  // Stripe's fold timing. The charged amount, the Stripe session, and every
  // receipt are unchanged.
  const deliveryTaxCents =
    !taxPendingShippingStep &&
    taxableScenario &&
    !isTaxExempt &&
    !isSkipShipping &&
    effectiveShippingPrice > 0
      ? roundToCents(effectiveShippingPrice * NY_SALES_TAX_RATE)
      : 0;
  const displaySubtotalCents = lineItemSubtotalCents;
  const taxAmount = productTaxCents + deliveryTaxCents;
  const payableTotal =
    displaySubtotalCents +
    effectiveShippingPrice +
    taxAmount -
    pricing.discount;
  // Make summaryItems reactive to checkout.total changes
  const summaryItems = useMemo(
    () => [
      {
        label: "Subtotal",
        value: formatStripePrice(displaySubtotalCents || 0),
      },
      ...(isSkipShipping
        ? []
        : [
            {
              label: "Shipping",
              value: shippingPendingAddress
                ? "Calculated at address"
                : formatStripePrice(effectiveShippingPrice),
            },
          ]),
      {
        label: "Discount",
        value: formatStripePrice(pricing.discount || 0),
      },
      {
        label:
          !taxPendingShippingStep && taxableScenario && !isTaxExempt
            ? "Sales Tax (8.875%)"
            : "Sales Tax",
        value: taxPendingShippingStep
          ? "Calculated at address"
          : formatStripePrice(taxAmount),
      },
      {
        label: "Total",
        value: formatStripePrice(payableTotal),
      },
    ],
    [
      effectiveShippingPrice,
      shippingPendingAddress,
      isSkipShipping,
      displaySubtotalCents,
      pricing.discount,
      taxableScenario,
      isTaxExempt,
      taxPendingShippingStep,
      taxAmount,
      payableTotal,
    ]
  );
  const isLoading =
    ctx.isUpdatingPricing ||
    ctx.isCalculatingShipping ||
    ctx.isFinalizingSession ||
    isCalculatingPrices ||
    isRevalidatingCheckout;
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await UserApi.getUser();
        if (!isMounted) return;
        setIsAdmin(Boolean(res?.data?.user?.isAdmin));
      } catch {
        if (!isMounted) return;
        setIsAdmin(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleToggleCheckoutLink = usePending(async () => {
    if (showCheckoutLink) {
      setShowCheckoutLink(false);
      return;
    }
    if (!lineItems.length) return;
    // Short link (2026-09-03): server mints secret + short code together.
    const { url } = await Crud.carts.shareLink(cart.id);
    revalidator.revalidate();
    setGeneratedCheckoutLink(url);
    setShowCheckoutLink(true);
  });
  const [
    customItemModalOpened,
    { open: openCustomItemModal, close: closeCustomItemModal },
  ] = useDisclosure(false);
  // On mobile the cart starts collapsed and can be expanded to the full view.
  const [isExpanded, { toggle: toggleExpanded }] = useDisclosure(false);
  // On mobile, the CONFIRM AND PAY button is rendered into a slot at the
  // bottom of the Checkout page (see Checkout.tsx) so it stays anchored there
  // while this component still owns the confirm handler and its guards.
  const [confirmSlot, setConfirmSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setConfirmSlot(
      isMobile ? document.getElementById("mobile-confirm-slot") : null
    );
  }, [isMobile]);
  const expandToggle = (
    <button
      onClick={toggleExpanded}
      style={{
        display: "flex",
        height: "30px",
        width: "100%",
        padding: "12px",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        borderRadius: "32px",
        background: "#E8F3F3",
        textTransform: "uppercase",
        color: "#213333",
        fontFamily: "Afacad",
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: "24px",
      }}
    >
      {isExpanded ? "View less" : "View cart"}
      <ChevronDownIcon
        className={cn(
          "size-4 transition-transform duration-200",
          isExpanded && "rotate-180"
        )}
      />
    </button>
  );
  // Shared promo-code input + price summary. Rendered inline on desktop and
  // portaled into the mobile confirm slot (see Checkout.tsx) on mobile.
  const promoAndSummary = (
    <>
      <p className="text-md mb-2 font-[600] leading-none text-[#191919]">
        Do you have a discount or gift card?
      </p>
      <div className="flex w-full max-w-[385px] gap-2">
        <TextInput
          value={ctx.data.promocode}
          placeholder="Enter here"
          onChange={e =>
            ctx.setData({ ...ctx.data, promocode: e.target.value })
          }
          onBlur={() =>
            !promoApplied &&
            ctx.data.promocode &&
            applyPromotionCode(ctx.data.promocode)
          }
          disabled={promoApplied}
          style={{ flex: 1 }}
        />
        <Button
          onClick={() => applyPromotionCode(ctx.data.promocode)}
          style={{ height: "auto", borderRadius: "10px" }}
          loading={!promoApplied && applyPromotionCode.isPending}
          disabled={promoApplied}
        >
          {promoApplied ? "Applied ✓" : "Apply"}
        </Button>
      </div>
      <div className="relative mt-6 grid gap-2 text-[16px] font-[600]">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-[6px] text-[#489B9B]">
              <Loader size="sm" color="#489B9B" />
              <span className="text-sm font-medium">Updating prices...</span>
            </div>
          </div>
        )}
        {summaryItems.map(({ label, value }, index, list) => {
          const isLast = index === list.length - 1;
          return (
            <div
              className={cn(
                "flex items-center justify-between",
                isLast && "border-t border-[#00000014] pt-5",
                isLoading && "opacity-50"
              )}
              key={label}
            >
              <p className="font-normal leading-none text-[#636363]">{label}</p>
              <p
                className={`font-[600] leading-none text-[#191919] ${isLast && "text-[20px]"}`}
              >
                {value}
              </p>
            </div>
          );
        })}
      </div>
    </>
  );
  // Compact mobile preview: item count + a horizontal strip of thumbnails,
  // shown while the cart is collapsed.
  const previewContent = (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between px-6 pt-6">
        <h2 className="text-xl font-semibold leading-[16px]">
          {printCount} {printCount > 1 ? "Items" : "Item"}
        </h2>
      </div>
      <div className="flex gap-2 overflow-x-auto px-6 py-4">
        {lineItems.map(lineItem => {
          const img =
            lineItem.itemJson?.compressedImageUrl ||
            lineItem.itemJson?.imageUrl;
          return img ? (
            <img
              key={lineItem.id}
              src={img}
              alt="Print preview"
              className="h-[80px] w-[80px] shrink-0 rounded-md object-cover"
            />
          ) : null;
        })}
      </div>
    </div>
  );
  return (
    <div
      className={`relative flex w-full min-w-0 flex-col gap-6 border-l border-[#00000014] bg-gray-0 md:mr-auto md:h-full md:max-w-[780px] md:overflow-auto`}
    >
      <CustomItemModal
        opened={customItemModalOpened}
        onClose={closeCustomItemModal}
        onSave={async () => {
          revalidator.revalidate();
          await CartService.getCart();
          closeCustomItemModal();
        }}
        cartId={cart.id}
      />
      {confirmSlot &&
        createPortal(
          (() => {
            const confirmDisabled =
              !ctx.reviewCompleted || isPendingUpdate || isRevalidatingCheckout;
            return (
              <div className="flex w-full flex-col border-t border-[#00000014] bg-white px-4 pb-4 pt-5">
                {promoAndSummary}
                <div className="mt-5 flex justify-center">
                  <Button
                    size="lg"
                    radius="xl"
                    w="356px"
                    maw="100%"
                    h="36px"
                    style={{
                      backgroundColor: confirmDisabled ? "#A8CDCD" : "#489B9B",
                      color: "white",
                      fontSize: "16px",
                      opacity: confirmDisabled ? 0.7 : 1,
                    }}
                    loading={
                      confirmPayment.isPending || ctx.isFinalizingSession
                    }
                    disabled={confirmDisabled}
                    onClick={confirmPayment}
                  >
                    CONFIRM AND PAY
                  </Button>
                </div>
              </div>
            );
          })(),
          confirmSlot
        )}
      {isMobile && (
        <Collapse in={!isExpanded} transitionDuration={300}>
          {previewContent}
        </Collapse>
      )}
      <MaybeAnimated isMobile={isMobile} isExpanded={isExpanded}>
        {/* Header is a 3-column grid with a MIN height (2026-09-11): the
            preview + cart-name column used to be absolutely positioned inside
            a fixed-height box, so a two-line cart name overflowed into item 1. */}
        <div
          className={`relative grid grid-cols-[1fr_auto_1fr] items-end px-6 pt-6 ${isAdmin ? "md:min-h-[120px] md:pt-2" : "md:min-h-[196px] md:pt-0"}`}
        >
          <h2 className="flex w-fit text-xl font-semibold leading-[16px]">
            {printCount} {printCount > 1 ? "Items" : "Item"}
          </h2>
          {!isMobile && (
            <div className="flex h-full max-w-[420px] flex-col items-center justify-end">
              {(lineItems[0]?.itemJson?.compressedImageUrl ||
                lineItems[0]?.itemJson?.imageUrl) && (
                <img
                  src={
                    lineItems[0].itemJson.compressedImageUrl ||
                    lineItems[0].itemJson.imageUrl
                  }
                  alt="Print preview"
                  className={`${isAdmin ? "h-[80px]" : "h-[160px]"} object-contain`}
                />
              )}
              {cart.isShareable && cart.name && (
                <p className="mt-1 max-w-full break-words rounded-full border border-[#00000014] bg-[#00000014] px-3 text-center text-xl font-bold leading-tight text-[#191919]">
                  {cart.name}
                </p>
              )}
            </div>
          )}
        </div>
        <div
          className={`flex flex-1 flex-col gap-4 px-6 ${isAdmin ? "" : "md:overflow-auto"}`}
        >
          {lineItems.map((lineItem, index) => (
            <Fragment key={lineItem.id}>
              {index !== 0 && <Divider />}
              <CartLineItem
                lineItem={lineItem}
                onUpdate={handleLineItemUpdate}
                onDelete={handleLineItemDelete}
                onSaved={async () => {
                  revalidator.revalidate();
                  await CartService.getCart();
                }}
                pendingDelete={pendingDeleteIds.has(lineItem.id)}
                isAdmin={isAdmin}
                showInsert
                shareSecret={shareSecret}
              />
            </Fragment>
          ))}
          {cart.message && (
            <>
              <Divider />
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold">Message</p>
                <p className="whitespace-pre-wrap rounded bg-gray-100 p-2 text-sm text-gray-700">
                  {cart.message}
                </p>
              </div>
            </>
          )}
          <div className="sticky bottom-0 bg-[#fafafa] pt-4">
            {isAdmin ? (
              <>
                <div className="flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-end">
                  <div className="flex flex-col gap-2 pb-2">
                    <Switch
                      label="Turn off sales tax"
                      checked={
                        pendingCartChanges.taxExempt !== undefined
                          ? pendingCartChanges.taxExempt
                          : !!(cart as any).taxExempt
                      }
                      onChange={e =>
                        setPendingCartChanges(prev => ({
                          ...prev,
                          taxExempt: e.currentTarget.checked,
                        }))
                      }
                      color="#4AA0A0"
                      size="sm"
                    />
                    {/* 2026-10-12: never on a shipping-only cart — it exists to price shipping. */}
                    {!(cart as any).shippingOnlyForOrderNo && (
                      <Switch
                        label="Turn off shipping calculations"
                        checked={
                          pendingCartChanges.skipShipping !== undefined
                            ? pendingCartChanges.skipShipping
                            : !!(cart as any).skipShipping
                        }
                        onChange={e =>
                          setPendingCartChanges(prev => ({
                            ...prev,
                            skipShipping: e.currentTarget.checked,
                          }))
                        }
                        color="#4AA0A0"
                        size="sm"
                      />
                    )}
                    <span className="rounded-full border border-[#e2e8f0] bg-white px-3 py-1 text-xs font-semibold text-[#475569] md:hidden">
                      Cart ID: {cart.id}
                    </span>
                  </div>
                  <span className="hidden rounded-full border border-[#e2e8f0] bg-white px-3 py-1 text-xs font-semibold text-[#475569] md:block">
                    Cart ID: {cart.id}
                  </span>
                  <div className="flex flex-col gap-2 pt-2">
                    {isPendingUpdate && (
                      <Button
                        onClick={calculateUpdatedPrices}
                        loading={calculateUpdatedPrices.isPending}
                        disabled={!isPendingUpdate}
                        style={{
                          height: "36px",
                          width: isPendingUpdate ? "180px" : "0px",
                          paddingInline: isPendingUpdate ? "18px" : "0px",
                          opacity: isPendingUpdate ? 1 : 0,
                          borderRadius: "10px",
                          backgroundColor: "#489B9B",
                          color: "white",
                          fontSize: "16px",
                          transition:
                            "width 0.3s ease, padding 0.3s ease, opacity 0.25s ease",
                        }}
                      >
                        Update Cart
                      </Button>
                    )}
                    <button
                      className="text-left text-sm text-[#4AA0A0] underline transition-opacity hover:opacity-70"
                      onClick={async () => {
                        // Lazy-load the PDF stack only when actually needed
                        // (keeps react-pdf out of the entry bundle).
                        const [{ pdf }, { CartPdf }] = await Promise.all([
                          import("@react-pdf/renderer"),
                          import("@/pages/DashboardCart/CartPdf"),
                        ]);
                        const toBase64 = async (
                          url: string
                        ): Promise<string> => {
                          const res = await fetch(url);
                          const blob = await res.blob();
                          return new Promise<string>(resolve => {
                            const reader = new FileReader();
                            reader.onload = () =>
                              resolve(reader.result as string);
                            reader.readAsDataURL(blob);
                          });
                        };
                        const imageMap: Record<string, string> = {};
                        await Promise.all(
                          cart.lineItems.map(async item => {
                            const imgUrl =
                              item.itemJson.compressedImageUrl ||
                              item.itemJson.imageUrl;
                            if (!imgUrl) return;
                            try {
                              imageMap[item.id] = await toBase64(imgUrl);
                            } catch {
                              // image unreachable — will fall back to placeholder
                            }
                          })
                        );
                        let logoBase64: string | undefined;
                        try {
                          logoBase64 = await toBase64("/logo.png");
                        } catch {
                          // logo unreachable — will fall back to text
                        }
                        const blob = await pdf(
                          <CartPdf
                            cart={cart}
                            imageMap={imageMap}
                            logoBase64={logoBase64}
                          />
                        ).toBlob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `cart-${(cart as any).name ? (cart as any).name.replace(/\s+/g, "-") : cart.id}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download PDF
                    </button>
                    <Button
                      onClick={() => {
                        window.location.href = "/visualizer";
                      }}
                      style={{
                        height: "36px",
                        width: "180px",
                        borderRadius: "10px",
                        color: "#636363",
                        backgroundColor: "#00000014",
                        fontSize: "16px",
                      }}
                    >
                      Create another print!
                    </Button>
                    <Button
                      radius="md"
                      size="sm"
                      leftSection={<PlusIcon className="size-4" />}
                      onClick={openCustomItemModal}
                    >
                      Create Custom Item
                    </Button>
                    {isShareable && (
                      <>
                        <Button
                          color="indigo"
                          leftSection={<ShareIcon className="size-4" />}
                          onClick={handleToggleCheckoutLink}
                          loading={handleToggleCheckoutLink.isPending}
                          disabled={!lineItems.length}
                          style={{ height: "36px", borderRadius: "10px" }}
                        >
                          {showCheckoutLink
                            ? "Hide checkout link"
                            : "Create checkout link"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {isAdmin && ctx.priceComparison?.length ? (
                  <PriceComparisonPanel rows={ctx.priceComparison} />
                ) : null}
                {showCheckoutLink && generatedCheckoutLink && (
                  <div className="mt-3 rounded-md border border-[#00000014] bg-white p-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#191919]">
                        Checkout Link
                      </p>
                      <TextInput
                        value={generatedCheckoutLink}
                        readOnly
                        style={{ flex: 1 }}
                      />
                      <Button
                        variant="light"
                        color={linkCopied ? "teal" : undefined}
                        leftSection={<Clipboard size={16} />}
                        onClick={() => {
                          navigator.clipboard.writeText(generatedCheckoutLink);
                          setLinkCopied(true);
                          setTimeout(() => setLinkCopied(false), 2000);
                        }}
                      >
                        {linkCopied ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between">
                <Button
                  onClick={() => {
                    window.location.href = "/visualizer";
                  }}
                  style={{
                    height: isMobile ? "40px" : "36px",
                    width: "182px",
                    borderRadius: isMobile ? "30px" : "10px",
                    color: "#636363",
                    backgroundColor: "#00000014",
                    paddingInline: isMobile ? "12px" : "18px",
                    fontSize: "16px",
                    textTransform: isMobile ? "uppercase" : "none",
                  }}
                >
                  Create another print!
                </Button>
                <Button
                  onClick={calculateUpdatedPrices}
                  loading={calculateUpdatedPrices.isPending}
                  disabled={!isPendingUpdate}
                  style={{
                    height: "36px",
                    width: isPendingUpdate ? "180px" : "0px",
                    paddingInline: isPendingUpdate ? "18px" : "0px",
                    opacity: isPendingUpdate ? 1 : 0,
                    borderRadius: "10px",
                    backgroundColor: "#489B9B",
                    color: "white",
                    fontSize: "16px",
                    transformOrigin: "right",
                    transition:
                      "width 0.3s ease, padding 0.3s ease, opacity 0.25s ease",
                  }}
                >
                  Update Cart
                </Button>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="bottom-0 left-0 flex w-full shrink-0 flex-col bg-white p-6 pb-4 transition-all duration-300 ease-in">
            {promoAndSummary}
            <Button
              size="lg"
              radius="xl"
              w="356px"
              maw="100%"
              h={ctx.reviewCompleted ? "50px" : "0px"}
              mt={ctx.reviewCompleted ? "24px" : "0px"}
              style={{
                backgroundColor: "#489B9B",
                color: "white",
                alignSelf: "center",
                transition: "height 0.3s ease, opacity 0.3s ease",
                opacity: ctx.reviewCompleted ? 1 : 0,
              }}
              loading={confirmPayment.isPending || ctx.isFinalizingSession}
              disabled={isPendingUpdate || isRevalidatingCheckout}
              onClick={confirmPayment}
            >
              CONFIRM AND PAY
            </Button>
          </div>
        )}
      </MaybeAnimated>
      {isMobile && <div className="px-6 pb-4">{expandToggle}</div>}
    </div>
  );
}
